import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Button, MenuItem, Divider, TablePagination,
} from '@mui/material';
import { ArrowBack, Add, Edit, Delete, MonetizationOn, ContentCopy, CopyAll } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const API = process.env.REACT_APP_API_URL;

const STUDENT_TYPES = ['Boarder', 'Day Scholar', 'All'];
const TERMS         = [1, 2, 3];

const emptyItem = (sortOrder = 1) => ({ _key: Date.now() + Math.random(), chargeItemCode: '', itemName: '', amount: '', sortOrder });

const emptyForm = {
  year: '', stageCode: '', term: 1, studentType: 'Boarder', items: [], status: 'Draft'
};

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value ?? <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const FeeTemplateList = () => {
  const navigate = useNavigate();

  const [templates,    setTemplates]   = useState([]);
  const [years,        setYears]       = useState([]);
  const [stages,       setStages]      = useState([]);
  const [chargeItems,  setChargeItems] = useState([]);
  const [yearFilter,   setYearFilter]  = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [success,     setSuccess]     = useState('');
  const [saving,      setSaving]      = useState(false);
  const [dialogOpen,  setDialogOpen]  = useState(false);
  const [editingId,   setEditingId]   = useState(null);
  const [form,        setForm]        = useState(emptyForm);
  const [formError,   setFormError]   = useState(''); // shown INSIDE the dialog
  const [viewTpl,     setViewTpl]     = useState(null);
  const [copyOpen,    setCopyOpen]    = useState(false);
  const [copyFrom,    setCopyFrom]    = useState('');
  const [copyTo,      setCopyTo]      = useState('');
  const [copying,     setCopying]     = useState(false);
  const [, setCopyResult] = useState(null);
  const [deleteDlg,   setDeleteDlg]   = useState(null); // { id, label }
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // Quick-add charge item from within a fee line (admin / academics-create rights)
  const [quickAddRow,  setQuickAddRow]  = useState(null);  // _key of the line that triggered it
  const [quickForm,    setQuickForm]    = useState({ code: '', name: '', sequence: '', applicableTo: 'Both' });
  const [quickSaving,  setQuickSaving]  = useState(false);
  const [quickError,   setQuickError]   = useState('');

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  // Guard the create/edit dialog: prompt before discarding unsaved edits,
  // same as the learner and charge-item forms. `form` includes the fee lines,
  // so adding/editing/removing a line marks the dialog dirty.
  const { attemptClose, discardDialog } = useUnsavedGuard({
    open: dialogOpen, value: form, onClose: () => setDialogOpen(false),
  });

  // ── Fetch supporting data ──────────────────────────────────────────────────
  const fetchAll = async () => {
    try {
      const [tRes, yRes, sRes, ciRes] = await Promise.all([
        axios.get(`${API}/api/academics/fee-templates`,  { headers }),
        axios.get(`${API}/api/academics/years`,          { headers }),
        axios.get(`${API}/api/stages`,                   { headers }),
        axios.get(`${API}/api/academics/charge-items?active=true`, { headers }),
      ]);
      setTemplates(tRes.data);
      setYears(yRes.data);
      setStages(sRes.data);
      setChargeItems(ciRes.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 2500);
    return () => clearTimeout(t);
  }, [success]);

  // Start each dialog session with a clean error banner.
  useEffect(() => { if (dialogOpen) setFormError(''); }, [dialogOpen]);

  // ── Filtered view ──────────────────────────────────────────────────────────
  const visible = templates.filter(t =>
    (!yearFilter  || t.year      === yearFilter)  &&
    (!stageFilter || t.stageCode === stageFilter)
  );
  const paged = visible.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  // ── Item helpers ───────────────────────────────────────────────────────────
  const addItem = () =>
    setForm(f => ({ ...f, items: [...f.items, emptyItem(f.items.length + 1)] }));

  const removeItem = (key) =>
    setForm(f => ({ ...f, items: f.items.filter(i => i._key !== key) }));

  const updateItem = (key, field, value) =>
    setForm(f => ({
      ...f,
      items: f.items.map(i => i._key === key ? { ...i, [field]: value } : i)
    }));

  // Pre-fill the fee lines from the real ACTIVE charge items that apply to the
  // selected student type — each row is linked to an actual charge item (code +
  // name), never a blank free-text row. Amounts are still left for the user to
  // enter. Charge items already on the template (by code) are not duplicated.
  const prefillCommon = () => {
    const st = form.studentType;
    const applicable = chargeItems.filter(ci => {
      if (st === 'Boarder')     return ci.applicableTo === 'Boarder'     || ci.applicableTo === 'Both';
      if (st === 'Day Scholar') return ci.applicableTo === 'Day Scholar' || ci.applicableTo === 'Both';
      return true; // 'All' → every active charge item
    });
    const existingCodes = new Set(form.items.map(i => i.chargeItemCode).filter(Boolean));
    const toAdd = applicable
      .filter(ci => !existingCodes.has(ci.code))
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
      .map((ci, idx) => ({
        _key: Date.now() + Math.random() + idx,
        chargeItemCode: ci.code,
        itemName: ci.name,
        amount: '',
        sortOrder: form.items.length + idx + 1,
      }));
    if (toAdd.length === 0) {
      setFormError('No more active charge items to add for this student type');
      return;
    }
    setForm(f => ({ ...f, items: [...f.items, ...toAdd] }));
  };

  const lineTotal = form.items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

  // ── Quick-add charge item (from a fee line's dropdown) ─────────────────────
  const openQuickAdd = (rowKey) => {
    const row = form.items.find(i => i._key === rowKey);
    const nextSeq = chargeItems.reduce((m, ci) => Math.max(m, ci.sequence || 0), 0) + 1;
    setQuickForm({
      code: (row?.itemName || '').toUpperCase().replace(/[^A-Z0-9]+/g, '').slice(0, 12),
      name: row?.itemName || '',
      sequence: nextSeq,
      applicableTo: form.studentType === 'All' ? 'Both' : (form.studentType || 'Both'),
    });
    setQuickError('');
    setQuickAddRow(rowKey);
  };

  const saveQuickAdd = async () => {
    if (!quickForm.code.trim() || !quickForm.name.trim() || !quickForm.sequence) {
      setQuickError('Code, name and sequence are required'); return;
    }
    setQuickSaving(true); setQuickError('');
    try {
      const res = await axios.post(`${API}/api/academics/charge-items`, {
        code: quickForm.code.trim().toUpperCase(),
        name: quickForm.name.trim(),
        sequence: parseInt(quickForm.sequence, 10),
        applicableTo: quickForm.applicableTo,
      }, { headers });
      const created = res.data;
      setChargeItems(list => [...list, created].sort((a, b) => (a.sequence || 0) - (b.sequence || 0)));
      // Link the new item to the fee line that triggered the quick-add
      setForm(f => ({
        ...f,
        items: f.items.map(i => i._key === quickAddRow
          ? { ...i, chargeItemCode: created.code, itemName: created.name }
          : i),
      }));
      setQuickAddRow(null);
      setSuccess(`Charge item ${created.code} created`);
    } catch (err) {
      setQuickError(err.response?.data?.error || 'Could not create charge item (admin or academics rights required)');
    } finally { setQuickSaving(false); }
  };

  // ── CRUD ───────────────────────────────────────────────────────────────────
  const handleCreate = () => {
    setEditingId(null);
    const activeYear = years.find(y => y.isActive)?.year || '';
    setForm({ ...emptyForm, year: activeYear, items: [] });
    setDialogOpen(true);
  };

  const handleEdit = (t) => {
    setEditingId(t._id);
    setForm({
      year: t.year, stageCode: t.stageCode, term: t.term,
      studentType: t.studentType, status: t.status,
      items: t.items.map(i => ({ ...i, chargeItemCode: i.chargeItemCode || '', _key: Date.now() + Math.random() }))
    });
    setDialogOpen(true);
  };

  const handleClone = (t) => {
    setEditingId(null);
    setForm({
      year: '', stageCode: t.stageCode, term: t.term,
      studentType: t.studentType, status: 'Draft',
      items: t.items.map(i => ({ ...i, chargeItemCode: i.chargeItemCode || '', _key: Date.now() + Math.random() }))
    });
    setDialogOpen(true);
  };

  const confirmDeleteTemplate = async () => {
    if (!deleteDlg) return;
    try {
      await axios.delete(`${API}/api/academics/fee-templates/${deleteDlg.id}`, { headers });
      setSuccess('Template deleted'); setDeleteDlg(null);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleSave = async () => {
    if (!form.year)      { setFormError('Academic Year is required'); return; }
    if (!form.stageCode) { setFormError('Stage is required');         return; }
    if (form.items.some(i => !i.itemName.trim())) {
      setFormError('All items must have a name'); return;
    }
    setSaving(true); setFormError('');
    try {
      const payload = {
        ...form,
        items: form.items.map((i, idx) => ({
          chargeItemCode: i.chargeItemCode || '',
          itemName:       i.itemName.trim(),
          amount:         parseFloat(i.amount) || 0,
          sortOrder:      idx + 1
        }))
      };
      if (editingId) {
        await axios.put(`${API}/api/academics/fee-templates/${editingId}`, payload, { headers });
        setSuccess('Template updated');
      } else {
        await axios.post(`${API}/api/academics/fee-templates`, payload, { headers });
        setSuccess('Template created');
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Copy Year ──────────────────────────────────────────────────────────────
  const handleCopyYear = async () => {
    if (!copyFrom)          { setError('Select a source year'); return; }
    if (!copyTo.trim())     { setError('Enter a target year');  return; }
    if (copyFrom === copyTo.trim()) { setError('Source and target must differ'); return; }
    setCopying(true); setError(''); setCopyResult(null);
    try {
      const res = await axios.post(
        `${API}/api/academics/fee-templates/copy-year`,
        { fromYear: copyFrom, toYear: copyTo.trim() },
        { headers }
      );
      const { copied, skipped } = res.data;
      fetchAll();
      setCopyOpen(false);
      setSuccess(
        `Copied ${copied} template${copied !== 1 ? 's' : ''} from ${copyFrom} → ${copyTo.trim()}` +
        (skipped > 0 ? ` · ${skipped} skipped (already existed)` : '')
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Copy failed');
    } finally {
      setCopying(false);
    }
  };

  const openCopyDialog = () => {
    const activeYear = years.find(y => y.isActive)?.year || (years[0]?.year || '');
    setCopyFrom(activeYear);
    setCopyTo('');
    setCopyResult(null);
    setError('');
    setCopyOpen(true);
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const stageName  = (code) => stages.find(s => s.code === code)?.name || code;
  const statusColor = { Draft: 'default', Active: 'success' };
  const tplLabel   = (t) => `${t.year} · ${t.stageCode} · Term ${t.term} · ${t.studentType}`;

  if (loading) return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>

          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <MonetizationOn color="primary" />
              <Box>
                <Typography variant="h5" color="primary">Fee Templates</Typography>
                <Typography variant="body2" color="text.secondary">
                  Per academic year · Per stage · Per term · Per student type
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1} alignItems="center">
              <Button
                variant="outlined" size="small" startIcon={<CopyAll />}
                onClick={openCopyDialog}
                title="Copy entire year's fee structure to a new year"
              >
                Copy Year
              </Button>
              <IconButton onClick={() => navigate('/academics')} title="Back to Academics">
                <ArrowBack />
              </IconButton>
              <IconButton onClick={handleCreate} title="New Template"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          {/* Filters */}
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <TextField select size="small" label="Filter by Year" value={yearFilter}
              onChange={e => { setYearFilter(e.target.value); setPage(0); }} sx={{ minWidth: 160 }}>
              <MenuItem value="">All Years</MenuItem>
              {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Filter by Stage" value={stageFilter}
              onChange={e => { setStageFilter(e.target.value); setPage(0); }} sx={{ minWidth: 200 }}>
              <MenuItem value="">All Stages</MenuItem>
              {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
            </TextField>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {/* Table */}
          {visible.length === 0 ? (
            <Typography color="text.secondary">
              {templates.length === 0
                ? 'No fee templates yet. Click + to create the first one.'
                : 'No templates match the selected filters.'}
            </Typography>
          ) : (
            <>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Year</strong></TableCell>
                  <TableCell><strong>Stage</strong></TableCell>
                  <TableCell align="center"><strong>Term</strong></TableCell>
                  <TableCell><strong>Student Type</strong></TableCell>
                  <TableCell align="center"><strong>Items</strong></TableCell>
                  <TableCell align="right"><strong>Total ({sym()})</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.map(t => (
                  <TableRow key={t._id} hover sx={{ cursor: 'pointer' }}
                    onClick={() => setViewTpl(t)}>
                    <TableCell><strong>{t.year}</strong></TableCell>
                    <TableCell>{stageName(t.stageCode)}</TableCell>
                    <TableCell align="center">
                      <Chip label={`Term ${t.term}`} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{t.studentType}</TableCell>
                    <TableCell align="center">{t.items?.length || 0}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {fmt(t.totalAmount)}
                    </TableCell>
                    <TableCell>
                      <Chip label={t.status}
                        color={statusColor[t.status] || 'default'}
                        size="small" variant="outlined" />
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <IconButton size="small" title="Clone to new year"
                        onClick={() => handleClone(t)}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="primary"
                        onClick={() => handleEdit(t)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error"
                        onClick={() => setDeleteDlg({ id: t._id, label: tplLabel(t) })}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={visible.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
            />
            </>
          )}
        </CardContent>
      </Card>

      {/* ── View Card ── */}
      <Dialog open={Boolean(viewTpl)} onClose={() => setViewTpl(null)} maxWidth="sm" fullWidth>
        {viewTpl && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <MonetizationOn color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>
                    {viewTpl.year} · {viewTpl.stageCode} · Term {viewTpl.term}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {stageName(viewTpl.stageCode)} · {viewTpl.studentType}
                  </Typography>
                </Box>
                <Box ml="auto">
                  <Chip label={viewTpl.status}
                    color={statusColor[viewTpl.status] || 'default'}
                    size="small" variant="outlined" />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box display="flex" gap={2} mb={1.5} flexWrap="wrap">
                <InfoRow label="Academic Year"  value={viewTpl.year} />
                <InfoRow label="Stage"          value={`${viewTpl.stageCode} — ${stageName(viewTpl.stageCode)}`} />
                <InfoRow label="Term"           value={`Term ${viewTpl.term}`} />
                <InfoRow label="Student Type"   value={viewTpl.studentType} />
              </Box>
              <Divider sx={{ mb: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Fee Items</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Item</strong></TableCell>
                    <TableCell align="right"><strong>Amount ({sym()})</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...viewTpl.items]
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.itemName}</TableCell>
                        <TableCell align="right">{fmt(item.amount)}</TableCell>
                      </TableRow>
                    ))}
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Total</strong></TableCell>
                    <TableCell align="right"><strong>{sym()} {fmt(viewTpl.totalAmount)}</strong></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </DialogContent>
            <DialogActions>
              <Button startIcon={<ContentCopy />} onClick={() => { setViewTpl(null); handleClone(viewTpl); }}>
                Clone
              </Button>
              <Button startIcon={<Edit />} onClick={() => { setViewTpl(null); handleEdit(viewTpl); }}>
                Edit
              </Button>
              <Button variant="outlined" onClick={() => setViewTpl(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Copy Year Dialog ── */}
      <Dialog open={copyOpen} onClose={() => { if (!copying) setCopyOpen(false); }} maxWidth="xs" fullWidth>
        <DialogTitle component="div">
          <Box display="flex" alignItems="center" gap={1}>
            <CopyAll color="primary" />
            <Typography variant="h6">Copy Fee Structure to New Year</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Copies all fee templates from the source year into the target year.
            Existing templates in the target year are skipped (not overwritten).
          </Typography>

          <Box display="flex" flexDirection="column" gap={2}>
            <TextField select size="small" label="Copy FROM (source year)"
              value={copyFrom} onChange={e => setCopyFrom(e.target.value)} fullWidth>
              <MenuItem value=""><em>Select source year</em></MenuItem>
              {years.map(y => (
                <MenuItem key={y.year} value={y.year}>
                  {y.year} ({templates.filter(t => t.year === y.year).length} templates)
                  {y.isActive ? ' · Active' : ''}
                </MenuItem>
              ))}
            </TextField>

            <TextField size="small" label="Copy TO (target year)" fullWidth
              value={copyTo}
              onChange={e => setCopyTo(e.target.value)}
              placeholder="e.g. 2025"
              helperText="Type the target year — it will be created if it doesn't exist yet"
            />
          </Box>

          {/* Preview count */}
          {copyFrom && (
            <Box mt={2} p={1.5} bgcolor="grey.50" borderRadius={1}>
              <Typography variant="body2">
                <strong>{templates.filter(t => t.year === copyFrom).length}</strong> templates
                will be copied from <strong>{copyFrom}</strong>
                {copyTo.trim() ? <> → <strong>{copyTo.trim()}</strong></> : ''}.
              </Typography>
            </Box>
          )}

          {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyOpen(false)} disabled={copying}>Close</Button>
          <Button variant="contained" startIcon={<CopyAll />}
            onClick={handleCopyYear} disabled={copying || !copyFrom || !copyTo.trim()}>
            {copying ? 'Copying…' : 'Copy All Templates'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Fee Template Confirmation Dialog */}
      <Dialog open={Boolean(deleteDlg)} onClose={() => setDeleteDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Fee Template</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteDlg?.label}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteTemplate}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── Create / Edit Dialog ── */}
      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit Fee Template' : 'New Fee Template'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>

          {formError && <Alert severity="error" sx={{ mt: 1, mb: 1 }}>{formError}</Alert>}

          {/* Header selectors (top-row caption clipping is handled globally in
              theme.js via the MuiDialogContent override). */}
          <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
            <TextField select size="small" label="Academic Year *"
              value={form.year} onChange={e => setForm(f => ({ ...f, year: e.target.value }))}
              disabled={!!editingId} sx={{ minWidth: 150 }}>
              <MenuItem value=""><em>Select Year</em></MenuItem>
              {years.map(y => (
                <MenuItem key={y.year} value={y.year}>
                  {y.year}{y.isActive ? ' (Active)' : ''}
                </MenuItem>
              ))}
            </TextField>

            <TextField select size="small" label="Stage *"
              value={form.stageCode} onChange={e => setForm(f => ({ ...f, stageCode: e.target.value }))}
              disabled={!!editingId} sx={{ minWidth: 200 }}>
              <MenuItem value=""><em>Select Stage</em></MenuItem>
              {stages.map(s => (
                <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>
              ))}
            </TextField>

            <TextField select size="small" label="Term *"
              value={form.term} onChange={e => setForm(f => ({ ...f, term: parseInt(e.target.value, 10) }))}
              disabled={!!editingId} sx={{ minWidth: 120 }}>
              {TERMS.map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
            </TextField>

            <TextField select size="small" label="Student Type *"
              value={form.studentType} onChange={e => setForm(f => ({ ...f, studentType: e.target.value }))}
              disabled={!!editingId} sx={{ minWidth: 160 }}>
              {STUDENT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>

            <TextField select size="small" label="Status"
              value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
              sx={{ minWidth: 120 }}>
              <MenuItem value="Draft">Draft</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
            </TextField>
          </Box>

          <Divider sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary">Fee Line Items</Typography>
          </Divider>

          {/* Quick-fill button */}
          <Box display="flex" justifyContent="flex-end" mb={1}>
            <Button size="small" variant="outlined" onClick={prefillCommon}>
              + Pre-fill Active Charge Items
            </Button>
          </Box>

          {/* Line items table */}
          <Table size="small" sx={{ mb: 1 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ width: 220 }}><strong>Charge Item</strong></TableCell>
                <TableCell><strong>Label (editable)</strong></TableCell>
                <TableCell align="right" sx={{ width: 160 }}><strong>Amount ({sym()})</strong></TableCell>
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {form.items.map(item => (
                <TableRow key={item._key}>
                  <TableCell>
                    <TextField select size="small" value={item.chargeItemCode || ''} fullWidth
                      onChange={e => {
                        if (e.target.value === '__new__') { openQuickAdd(item._key); return; }
                        const ci = chargeItems.find(c => c.code === e.target.value);
                        setForm(f => ({
                          ...f,
                          items: f.items.map(i => i._key === item._key
                            ? { ...i, chargeItemCode: e.target.value, itemName: ci ? ci.name : i.itemName }
                            : i)
                        }));
                      }}>
                      <MenuItem value=""><em>— Custom / no link —</em></MenuItem>
                      <MenuItem value="__new__" sx={{ color: 'primary.main', fontWeight: 700 }}>
                        <Add fontSize="small" sx={{ mr: 0.5 }} /> New charge item…
                      </MenuItem>
                      <Divider />
                      {chargeItems
                        .filter(ci => ci.applicableTo === 'Both' || ci.applicableTo === form.studentType || form.studentType === 'All')
                        .map(ci => (
                          <MenuItem key={ci.code} value={ci.code}>
                            {ci.sequence}. {ci.code} — {ci.name}
                          </MenuItem>
                        ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small" value={item.itemName} fullWidth
                      placeholder="e.g. Tuition Fee"
                      onChange={e => updateItem(item._key, 'itemName', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small" type="number" value={item.amount} fullWidth
                      inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                      onChange={e => updateItem(item._key, 'amount', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" color="error" onClick={() => removeItem(item._key)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {/* Total row */}
              {form.items.length > 0 && (
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell><strong>Total</strong></TableCell>
                  <TableCell align="right"><strong>{sym()} {fmt(lineTotal)}</strong></TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>

          <Button size="small" startIcon={<Add />} onClick={addItem} variant="outlined">
            Add Item
          </Button>

          {form.items.length === 0 && (
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Click "Pre-fill Common Items" to load standard fee items, then adjust amounts.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Quick-add Charge Item (from a fee line) ── */}
      <Dialog open={Boolean(quickAddRow)} onClose={() => { if (!quickSaving) setQuickAddRow(null); }} maxWidth="xs" fullWidth>
        <DialogTitle component="div">
          <Box display="flex" alignItems="center" gap={1}>
            <Add color="primary" />
            <Typography variant="h6">New Charge Item</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {quickError && <Alert severity="error" sx={{ mb: 2 }}>{quickError}</Alert>}
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField size="small" label="Name *" fullWidth autoFocus
              value={quickForm.name}
              onChange={e => setQuickForm(q => ({
                ...q,
                name: e.target.value,
                // Keep the code in sync while the user hasn't customised it
                code: q.code === q.name.toUpperCase().replace(/[^A-Z0-9]+/g, '').slice(0, 12)
                  ? e.target.value.toUpperCase().replace(/[^A-Z0-9]+/g, '').slice(0, 12)
                  : q.code,
              }))}
              placeholder="e.g. Swimming Lessons" />
            <TextField size="small" label="Code *" fullWidth
              value={quickForm.code}
              onChange={e => setQuickForm(q => ({ ...q, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') }))}
              helperText="Short unique code, e.g. SWIM" />
            <Box display="flex" gap={2}>
              <TextField size="small" label="Sequence *" type="number" sx={{ width: 120 }}
                value={quickForm.sequence}
                onChange={e => setQuickForm(q => ({ ...q, sequence: e.target.value }))}
                helperText="Print order" />
              <TextField select size="small" label="Applies To" sx={{ flex: 1 }}
                value={quickForm.applicableTo}
                onChange={e => setQuickForm(q => ({ ...q, applicableTo: e.target.value }))}>
                <MenuItem value="Both">Both</MenuItem>
                <MenuItem value="Boarder">Boarder</MenuItem>
                <MenuItem value="Day Scholar">Day Scholar</MenuItem>
              </TextField>
            </Box>
            <Typography variant="caption" color="text.secondary">
              GL account and VAT settings can be added later under Academics → Charge Items.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickAddRow(null)} disabled={quickSaving}>Cancel</Button>
          <Button variant="contained" onClick={saveQuickAdd} disabled={quickSaving}>
            {quickSaving ? 'Creating…' : 'Create & Use'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeeTemplateList;
